/* ez-header — instance-safe header behavior.
   - Dropdowns: hover (desktop) + click/keyboard toggle + focus-within, closed on outside
     click / Escape. Touch-first: the first tap on a dropdown trigger opens it instead of
     navigating; a second tap follows the link.
   - Mobile drawer: open/close, focus trap-lite, Escape, body scroll lock.
   - Search: the trigger emits `ez:search:open` (the search packet owns the drawer).
   - Cart: refreshes {{ cart.item_count }} on `ez:cart:refresh` / `ezsnap-cart` / storage
     via a /cart.js re-read, so the count stays live after AJAX add-to-cart.
   Guards against double-init on Shopify section re-render (theme editor). */
(function () {
  "use strict";

  function initHeader(root) {
    if (!root || root.__ezHeaderInit) return;
    root.__ezHeaderInit = true;

    var mqDesktop = window.matchMedia("(min-width: 901px)");
    var isTouch = window.matchMedia("(hover: none)").matches;

    /* ── Dropdowns ─────────────────────────────── */
    var dropdowns = Array.prototype.slice.call(root.querySelectorAll("[data-ez-dropdown]"));

    function closeAll(except) {
      dropdowns.forEach(function (dd) {
        if (dd === except) return;
        if (dd.classList.contains("is-open")) {
          dd.classList.remove("is-open");
          var t = dd.querySelector("[data-ez-dropdown-trigger]");
          if (t) t.setAttribute("aria-expanded", "false");
        }
      });
    }

    function open(dd) {
      dd.classList.add("is-open");
      var t = dd.querySelector("[data-ez-dropdown-trigger]");
      if (t) t.setAttribute("aria-expanded", "true");
    }
    function close(dd) {
      dd.classList.remove("is-open");
      var t = dd.querySelector("[data-ez-dropdown-trigger]");
      if (t) t.setAttribute("aria-expanded", "false");
    }

    dropdowns.forEach(function (dd) {
      var trigger = dd.querySelector("[data-ez-dropdown-trigger]");
      if (!trigger) return;

      // Hover (desktop, pointer devices)
      dd.addEventListener("mouseenter", function () {
        if (mqDesktop.matches && !isTouch) { closeAll(dd); open(dd); }
      });
      dd.addEventListener("mouseleave", function () {
        if (mqDesktop.matches && !isTouch) close(dd);
      });

      // Click / touch: first tap opens, second follows the link
      trigger.addEventListener("click", function (e) {
        if (!mqDesktop.matches) return; // mobile uses the drawer
        var wasOpen = dd.classList.contains("is-open");
        if (!wasOpen) {
          e.preventDefault();
          closeAll(dd);
          open(dd);
        }
        // if already open, let the click navigate
      });

      // Keyboard: focus-within opens; Escape closes back to the trigger
      dd.addEventListener("focusin", function () {
        if (mqDesktop.matches) { closeAll(dd); open(dd); }
      });
      dd.addEventListener("focusout", function (e) {
        if (!dd.contains(e.relatedTarget)) close(dd);
      });
      dd.addEventListener("keydown", function (e) {
        if (e.key === "Escape") { close(dd); trigger.focus(); }
      });
    });

    document.addEventListener("click", function (e) {
      if (!root.contains(e.target)) closeAll(null);
      else if (!e.target.closest("[data-ez-dropdown]")) closeAll(null);
    });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape") closeAll(null);
    });

    /* ── Mobile drawer ─────────────────────────── */
    var drawer = root.querySelector("[data-ez-drawer]");
    var openBtn = root.querySelector("[data-ez-drawer-open]");
    var closeBtn = root.querySelector("[data-ez-drawer-close]");

    function openDrawer() {
      if (!drawer) return;
      drawer.hidden = false;
      if (openBtn) openBtn.setAttribute("aria-expanded", "true");
      document.documentElement.style.overflow = "hidden";
      var firstLink = drawer.querySelector("a, button");
      if (firstLink) firstLink.focus();
    }
    function closeDrawer() {
      if (!drawer) return;
      drawer.hidden = true;
      if (openBtn) { openBtn.setAttribute("aria-expanded", "false"); openBtn.focus(); }
      document.documentElement.style.overflow = "";
    }
    if (openBtn) openBtn.addEventListener("click", openDrawer);
    if (closeBtn) closeBtn.addEventListener("click", closeDrawer);
    if (drawer) {
      drawer.addEventListener("keydown", function (e) {
        if (e.key === "Escape") closeDrawer();
      });
    }

    /* ── Search trigger → hand off to the search packet ── */
    var searchBtns = root.querySelectorAll("[data-ez-search-open]");
    Array.prototype.forEach.call(searchBtns, function (btn) {
      btn.addEventListener("click", function () {
        document.dispatchEvent(new CustomEvent("ez:search:open"));
      });
    });

    /* ── Cart count refresh ───────────────────── */
    var countEl = root.querySelector("[data-ez-cart-count]");
    // Build-aware count: each configured build (hidden _build prop) = 1; standalone lines count by qty.
    // Mirrors snippets/ez-cart-count.liquid so the badge stays right after a cart mutation (raw
    // cart.item_count summed the hidden snap/stud pack lines, showing e.g. "6" for a 2-build cart).
    function ezBuildCount(cart) {
      var seen = {}, n = 0;
      (cart.items || []).forEach(function (it) {
        var p = it.properties || {}, bid = p._build || "";
        if (!bid) { n += it.quantity || 1; }
        else if (!seen[bid]) { seen[bid] = 1; n += 1; }
      });
      return n;
    }
    function refreshCart() {
      if (!countEl) return;
      fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + "cart.js" : "/cart.js", {
        headers: { Accept: "application/json" }
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) {
          if (!cart) return;
          var n = ezBuildCount(cart);
          countEl.textContent = n;
          countEl.classList.toggle("is-empty", (cart.item_count || 0) === 0);
        })
        .catch(function () {});
    }
    document.addEventListener("ez:cart:refresh", refreshCart);
    // The cart drawer / islands announce mutations — keep the header count in sync.
    document.addEventListener("ez:cart:added", refreshCart);
    document.addEventListener("ez:cart:updated", refreshCart);
    window.addEventListener("ezsnap-cart", refreshCart);

    /* ── Cart trigger → open the slide-over drawer (link stays a no-JS fallback) ── */
    var cartLink = root.querySelector("[data-ez-cart-link]");
    if (cartLink) {
      cartLink.addEventListener("click", function (e) {
        // Only hijack when the drawer chrome is present on the page.
        if (!document.querySelector("[data-ez-cart-drawer]")) return;
        e.preventDefault();
        document.dispatchEvent(new CustomEvent("ez:cart:open"));
      });
    }
    window.addEventListener("storage", function (e) {
      if (!e || !e.key || e.key === "cart" || e.key.indexOf("ezsnap") === 0) refreshCart();
    });
  }

  function boot() {
    var headers = document.querySelectorAll("[data-ez-header]");
    Array.prototype.forEach.call(headers, initHeader);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  // Theme editor: re-init on section load
  document.addEventListener("shopify:section:load", function (e) {
    var h = e.target.querySelector("[data-ez-header]");
    if (h) initHeader(h);
  });
})();
